import { useQuery } from "@tanstack/react-query";

import { getPlanningSettings } from "@/api/settings";
import { getWarehouses } from "@/api/warehouses";

/**
 * Which rung of the centring ladder produced the current view (TASK-4997).
 *
 * Exported so a test can assert the ladder reached the rung it was supposed to
 * reach, rather than inferring it from coordinates that two rungs could both
 * have produced.
 */
export type MapCenterSource =
  | "order"
  | "defaultWarehouse"
  | "warehouseCentroid"
  | "region";

export interface MapCenterResult {
  /** Always a point: the last rung frames the operating region, so there is no un-centred case. */
  center: [number, number];
  zoom: number;
  source: MapCenterSource;
}

/**
 * Zoom levels per rung. These are Leaflet zoom integers, not coordinates — no
 * location is asserted by any of them.
 *
 * `ORDER_ZOOM` matches the 14 the marker branch has always used, so an order
 * that HAS coordinates keeps exactly today's view. The tenant rungs sit one
 * step wider because they describe a depot area rather than a doorway.
 *
 * There is deliberately NO rung that resolves the tenant's city name into a
 * point by calling the geocoder (OWNER DECISION, 2026-09-24). That would cost a
 * forward-geocode request against the shared 1 req/s Nominatim budget — spent
 * merely to decide where to aim an EMPTY map — and it is reached only by a
 * tenant with no geocoded warehouse at all, i.e. one still being set up.
 *
 * The last rung is instead a fixed view of the platform's operating region
 * (OWNER, 2026-09-24: "надо Беларусь чтобы хотя бы показывало"). A world view
 * turned out to be useless in practice: the dispatcher had to pan across the
 * globe before they could place anything.
 *
 * **This constant is NOT the §10.10 "no hardcoded location" case.** That rule is
 * about never asserting a location the ORDER does not have — i.e. never dropping
 * a marker somewhere invented. This is the opposite: no marker is rendered, and
 * the value only frames an empty, pannable map over the region this platform
 * serves (Belarus + PL/LT/LV, see CLAUDE.md § Project Overview).
 */
export const ORDER_ZOOM = 14;
export const TENANT_ZOOM = 12;
/**
 * Fallback view when nothing about the tenant is known: the operating region,
 * centred on Belarus at a zoom that shows the whole country and its borders.
 */
export const REGION_CENTER: [number, number] = [53.7, 27.95];
export const REGION_ZOOM = 6;

/**
 * Parses a backend coordinate, which arrives as a DRF DecimalField STRING
 * ("53.902496"), never as a number (MEMORY: reference_drf_decimal_string_frontend).
 *
 * `0` is rejected along with `null` and unparseable text: the app-wide
 * convention is that `(0, 0)` means "not set", and a warehouse pinned to the
 * Gulf of Guinea would drag a centroid halfway across the Atlantic.
 */
function parseCoord(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  if (!isFinite(parsed) || parsed === 0) return null;
  return parsed;
}

interface Options {
  /** The order's own coordinates, when it has them (rung 1). */
  lat: number | null;
  lon: number | null;
  /**
   * Skip every network rung. The hosts mount this block inside modals that are
   * often closed; there is no reason to fetch a tenant's warehouse list to
   * centre a map nobody is looking at.
   */
  enabled: boolean;
}

/**
 * Resolves what an EMPTY (markerless) order map should be centred on, as the
 * four-rung ladder TASK-4997 specifies:
 *
 *   1. the order's own coordinates, when present;
 *   2. the tenant's `default_start_warehouse`, when set and geocoded;
 *   3. the centroid of the tenant's geocoded warehouses, when no default is set;
 *   4. the tenant's `preferred_country` / `preferred_city`, resolved through the
 *      existing server-side geocoder — and a world view when even that is blank.
 *
 * Rung 3 is load-bearing, not a nicety: tenant 12 (Dobronom) has NO
 * `default_start_warehouse` while 10/10 of its warehouses do have coordinates,
 * so rung 2 misses and rung 4 would show that tenant a country-level view of a
 * city they work in every day.
 *
 * **There is no hardcoded city anywhere in this ladder.** Rung 4 asks the
 * backend geocoder for the tenant's OWN configured area instead of shipping a
 * literal coordinate pair, which is what keeps a Minsk (or Warsaw) constant out
 * of the codebase — a constant would quietly assert a location for every tenant
 * that never set a preference.
 */
export function useMapCenterLadder({
  lat,
  lon,
  enabled,
}: Options): MapCenterResult {
  const hasOrderCoords = lat !== null && lon !== null;
  // Rung 1 short-circuits every request: when the order says where it is, no
  // tenant lookup can improve on that.
  const needsTenantData = enabled && !hasOrderCoords;

  const { data: planningSettings } = useQuery({
    queryKey: ["settings-planning"],
    queryFn: getPlanningSettings,
    enabled: needsTenantData,
    staleTime: 5 * 60 * 1000,
  });

  const defaultWarehouse = planningSettings?.default_start_warehouse ?? null;
  const defaultLat = parseCoord(defaultWarehouse?.lat);
  const defaultLon = parseCoord(defaultWarehouse?.lon);
  const hasDefaultWarehouse = defaultLat !== null && defaultLon !== null;

  // Rung 3 only runs when rung 2 missed. Waiting for `planningSettings` to
  // arrive before deciding avoids fetching the warehouse list for every tenant
  // that does have a default depot.
  const needsWarehouses =
    needsTenantData && planningSettings !== undefined && !hasDefaultWarehouse;

  const { data: warehousePage } = useQuery({
    queryKey: ["warehouses", "map-center-ladder"],
    queryFn: () => getWarehouses({ page_size: 100 }),
    enabled: needsWarehouses,
    staleTime: 5 * 60 * 1000,
  });

  const geocodedWarehouses = (warehousePage?.results ?? [])
    .map((warehouse) => ({
      lat: parseCoord(warehouse.lat),
      lon: parseCoord(warehouse.lon),
    }))
    .filter(
      (point): point is { lat: number; lon: number } =>
        point.lat !== null && point.lon !== null,
    );

  const hasCentroid = geocodedWarehouses.length > 0;

  if (hasOrderCoords) {
    return { center: [lat, lon], zoom: ORDER_ZOOM, source: "order" };
  }
  if (hasDefaultWarehouse) {
    return {
      center: [defaultLat, defaultLon],
      zoom: TENANT_ZOOM,
      source: "defaultWarehouse",
    };
  }
  if (hasCentroid) {
    const sum = geocodedWarehouses.reduce(
      (acc, point) => ({ lat: acc.lat + point.lat, lon: acc.lon + point.lon }),
      { lat: 0, lon: 0 },
    );
    return {
      center: [
        sum.lat / geocodedWarehouses.length,
        sum.lon / geocodedWarehouses.length,
      ],
      zoom: TENANT_ZOOM,
      source: "warehouseCentroid",
    };
  }
  // Nothing describes this tenant. Frame the operating region rather than the
  // globe: no marker is placed, so nothing is asserted about the order — the
  // dispatcher just starts from somewhere useful instead of mid-ocean.
  return { center: REGION_CENTER, zoom: REGION_ZOOM, source: "region" };
}
